import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Copy, X } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { copyText } from './copy-text';

interface RoomSettingsRow {
  guests_enabled: boolean;
  lobby_enabled: boolean;
  locked: boolean;
  max_participants: number;
}

const CAPACITIES = [2, 3, 4, 5, 6, 8, 10, 12] as const;

/**
 * Réglages d'hôte d'une salle EXISTANTE (cours collectif OU réunion libre) —
 * activer les invités, la salle d'attente, verrouiller, et générer/copier le
 * lien d'invitation (`create_visio_invite`, réutilise le lien actif — migration
 * 0259). Host-only : `RoomPage` ne le monte jamais pour un non-hôte.
 *
 * `buildInviteUrl` est INJECTÉ (comme `db`/`onExit` ailleurs dans ce package) :
 * l'origine du lien partagé diffère selon l'app appelante
 * (`apps/app` → `tutor.jeraisonne.fr` construit côté app ; `apps/tutor` →
 * sa propre origine).
 */
export function RoomSettingsPanel({
  roomId,
  db,
  buildInviteUrl,
  onClose,
}: {
  roomId: string;
  db: SupabaseClient;
  buildInviteUrl: (token: string) => string;
  onClose: () => void;
}) {
  const [settings, setSettings] = useState<RoomSettingsRow | null>(null);
  const [admittedCount, setAdmittedCount] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [settingsRes, countRes] = await Promise.all([
        db
          .from('video_rooms')
          .select('guests_enabled, lobby_enabled, locked, max_participants')
          .eq('id', roomId)
          .maybeSingle(),
        db
          .from('video_room_participants')
          .select('user_id', { count: 'exact', head: true })
          .eq('room_id', roomId)
          .eq('status', 'admitted'),
      ]);
      if (cancelled) return;
      if (settingsRes.error || !settingsRes.data) {
        setLoadError('Impossible de charger les réglages.');
        return;
      }
      setSettings(settingsRes.data as RoomSettingsRow);
      // Best-effort : l'occupation n'est qu'indicative, une erreur ne bloque pas les réglages.
      setAdmittedCount(countRes.error ? null : (countRes.count ?? null));
    })();
    return () => {
      cancelled = true;
    };
  }, [db, roomId]);

  async function updateSetting(patch: Partial<RoomSettingsRow>) {
    if (!settings) return;
    const previous = settings;
    setSettings({ ...settings, ...patch });
    setSaveError(null);
    const { error } = await db.rpc('set_visio_room_settings', {
      p_room_id: roomId,
      p_guests_enabled: patch.guests_enabled ?? null,
      p_lobby_enabled: patch.lobby_enabled ?? null,
      p_locked: patch.locked ?? null,
      p_max_participants: patch.max_participants ?? null,
    });
    if (error) {
      setSettings(previous);
      setSaveError(
        error.message.includes('max_below_admitted')
          ? `Impossible de descendre sous ${admittedCount ?? previous.max_participants} (déjà admis).`
          : 'Réglage refusé. Réessaie.',
      );
    }
  }

  async function handleGenerateLink() {
    setGenerating(true);
    setLinkError(null);
    const { data, error } = await db.rpc('create_visio_invite', { p_room_id: roomId });
    setGenerating(false);
    if (error) {
      setLinkError('Impossible de générer le lien.');
      return;
    }
    const row = (Array.isArray(data) ? data[0] : data) as { token: string } | undefined;
    if (!row?.token) {
      setLinkError('Impossible de générer le lien.');
      return;
    }
    setLink(buildInviteUrl(row.token));
  }

  async function handleCopy() {
    if (!link) return;
    const ok = await copyText(link);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="room-settings-title"
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80dvh] w-full max-w-md space-y-4 overflow-y-auto rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:rounded-2xl"
      >
        <div className="flex items-center justify-between">
          <p id="room-settings-title" className="text-ink-900 text-base font-bold">
            Réglages de la salle
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer les réglages"
            className="text-ink-500 flex h-11 w-11 items-center justify-center rounded-full active:scale-95"
          >
            <X className="size-5" />
          </button>
        </div>

        {loadError && <p className="text-accent-700 text-xs font-medium">{loadError}</p>}

        {settings && (
          <div className="space-y-2">
            <div>
              <label
                htmlFor="room-settings-capacity"
                className="text-ink-700 mb-1.5 block text-xs font-semibold"
              >
                Nombre de participants max
                {admittedCount != null && (
                  <span className="text-ink-500 font-normal">
                    {' '}
                    · {admittedCount}/{settings.max_participants} admis
                  </span>
                )}
              </label>
              <select
                id="room-settings-capacity"
                value={settings.max_participants}
                onChange={(e) => void updateSetting({ max_participants: Number(e.target.value) })}
                className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
              >
                {CAPACITIES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              {admittedCount != null && admittedCount >= settings.max_participants && (
                <p className="text-accent-700 mt-1.5 text-xs">
                  La salle est pleine — augmente ce nombre pour pouvoir admettre quelqu'un de plus.
                </p>
              )}
            </div>

            <SettingToggle
              label="Autoriser les invités"
              description="Sans compte JeRaisonne — juste un nom."
              checked={settings.guests_enabled}
              onChange={(v) => void updateSetting({ guests_enabled: v })}
            />
            <SettingToggle
              label="Salle d'attente"
              description="Tu admets chaque personne avant qu'elle n'entre."
              checked={settings.lobby_enabled}
              onChange={(v) => void updateSetting({ lobby_enabled: v })}
            />
            <SettingToggle
              label="Verrouiller la salle"
              description="Plus personne ne peut entrer, même avec le lien."
              checked={settings.locked}
              onChange={(v) => void updateSetting({ locked: v })}
            />
          </div>
        )}

        {saveError && (
          <p role="alert" className="text-accent-700 text-xs font-medium">
            {saveError}
          </p>
        )}

        <div className="border-ink-100 space-y-2 border-t pt-3">
          <p className="text-ink-700 text-xs font-semibold">Lien d'invitation</p>
          {link ? (
            <div className="space-y-2">
              <input
                readOnly
                value={link}
                aria-label="Lien d'invitation"
                onFocus={(e) => e.currentTarget.select()}
                className="border-ink-200 bg-ink-50 text-ink-700 h-11 w-full rounded-xl border px-3 font-mono text-xs focus:outline-none"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 w-full"
                onClick={() => void handleCopy()}
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copié !' : 'Copier le lien'}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="min-h-[2.75rem] w-full"
              onClick={() => void handleGenerateLink()}
              disabled={generating}
            >
              {generating ? 'Génération…' : "Générer un lien d'invitation"}
            </Button>
          )}
          {linkError && (
            <p role="alert" className="text-accent-700 text-xs font-medium">
              {linkError}
            </p>
          )}
        </div>

        <Button onClick={onClose} className="min-h-[2.75rem] w-full">
          Fermer
        </Button>
      </div>
    </div>
  );
}

function SettingToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex min-h-[2.75rem] w-full items-center justify-between rounded-xl border px-3.5 text-left transition-colors active:scale-[0.98]',
        checked ? 'border-primary-500 bg-primary-50' : 'border-ink-200 bg-white',
      )}
    >
      <span>
        <span className="text-ink-900 block text-sm font-semibold">{label}</span>
        <span className="text-ink-500 block text-xs">{description}</span>
      </span>
      <span
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full border-2',
          checked ? 'border-primary-500 bg-primary-500' : 'border-ink-300',
        )}
      >
        {checked && <span className="size-2.5 rounded-full bg-white" />}
      </span>
    </button>
  );
}
